import { aiUsageFetch } from "../../lib/aiUsageApi";
import { AUTO_DIGITIZE_API } from "../../config/stitchServices";
import { parse, stringify } from "svgson";
import { ref, uploadString } from "firebase/storage";
import { doc, writeBatch, serverTimestamp } from "firebase/firestore";
import {
  auth,
  db,
  storage,
  saveStitchStateToFirestore,
  saveStitchFileToStorage,
} from "../../lib/firebase/firebase";
import {
  SVG_FOLDER,
  REFERENCE_ARTWORK_FOLDER,
  METADATA_COLLECTION,
  FILES_SUBCOLLECTION,
} from "../../config/firebaseVariant";
import { processAndAnalyzeImage } from "../../components/Converter/components/AutoUploadImage";
import { processImage } from "../../components/Converter/components/processImage";
import { prepareDesign } from "../../components/StitchEditor/designOrchestrator";
import {
  materializeVideoRails,
  materializeAvailableRails,
  isCollapsedSatinPiece,
} from "../../components/StitchEditor/astraSatinClient";
import { parseEmbroideryFile } from "../../components/SvgEditor/components/stitchParser";
import { playgroundRecord } from "./storage";
import { assembleDesign } from "./geometry";
import { cropArtwork } from "./cropArtwork";

const API = AUTO_DIGITIZE_API;
export async function materializeWithRepair(result = {}, signal, report) {
  try {
    return materializeVideoRails(result);
  } catch (originalError) {
    const pieces = [],
      cuts = [...(result.cuts || [])];
    for (const piece of result.pieces || []) {
      if (signal.aborted) throw new DOMException("Paused", "AbortError");
      if (isCollapsedSatinPiece(piece)) continue;
      try {
        materializeVideoRails({ ...result, pieces: [piece] });
        pieces.push(piece);
      } catch {
        report(`Refining satin section ${piece.id} locally…`);
        try {
          const response = await jobRequest("/repair-section", { piece }, signal);
          let accepted;
          for (const alternative of response.alternatives || []) {
            try {
              materializeVideoRails(alternative);
              accepted = alternative;
              break;
            } catch {
              /* Try another coverage-checked partition, keeping validation strict. */
            }
          }
          if (!accepted) throw originalError;
          pieces.push(...accepted.pieces);
          cuts.push(...accepted.cuts);
        } catch (error) {
          if (signal.aborted || error.name === "AbortError") throw error;
          pieces.push(piece);
        }
      }
    }
    return materializeAvailableRails({
      ...result,
      pieces,
      cuts,
      localRailRepair: true,
    });
  }
}
export async function jobRequest(path, body, signal) {
  const response = await aiUsageFetch(`${API}${path}`, {
    method: body ? "POST" : "GET",
    headers: { "Content-Type": "application/json" },
    signal,
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok)
    throw new Error(
      `The auto-digitize service is unavailable (${response.status}). Your progress is saved.`
    );
  return response.json();
}

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    const stop = () => {
      clearTimeout(timer);
      reject(new DOMException("Paused", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", stop);
      resolve();
    }, ms);
    signal.addEventListener("abort", stop, { once: true });
    if (signal.aborted) stop();
  });

export function dstPreview(run) {
  if (!run.dst?.data) return null;
  const parsed = parseEmbroideryFile(run.dst.data, "dst");
  // DST has no color table. Use the accompanying project thread order.
  if (parsed)
    parsed.colors = run.artifact.svg.children.map(
      (c) => c.attributes.fill || "#333333"
    );
  return parsed;
}

export async function executePlayground(initial, signal, update) {
  let run = initial;
  const check = () => {
    if (signal.aborted || auth.currentUser?.uid !== run.uid)
      throw new DOMException("Paused", "AbortError");
  };
  const report = (progress) => {
    check();
    update({ ...run, progress });
  };
  const checkpoint = async (patch) => {
    check();
    run = { ...run, ...patch, error: null };
    await playgroundRecord(run.uid, run);
    check();
    update(run);
  };
  try {
    check();
    // A durable job ID is immutable. Retrying a failed provider job therefore
    // needs a fresh backend ID while keeping the completed trace and prepared
    // layer input from the saved run.
    if (run.error && run.input && !run.result) {
      await checkpoint({
        jobId:
          window.crypto?.randomUUID?.() ||
          `auto-job-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        startedAt: Date.now(),
        costUsage: null,
        progress: "Retrying the saved layer plan…",
      });
    }
    if (!run.result) await jobRequest("/health", null, signal);
    if (!run.svg) {
      report("Finding thread colors…");
      const source = await processAndAnalyzeImage(run.image);
      check();
      if (!source.colors?.length || source.colors.length > 24)
        throw new Error("Choose artwork with 1–24 colors for auto-digitizing.");
      report("Tracing your image into color layers…");
      const traced = await processImage({
        ...source,
        smoothness: 1,
        simplifyCurves: 0.2,
        despeckle: 2,
        ai: false,
        useSuperpixels: true,
        useMorphology: false,
      });
      URL.revokeObjectURL(traced.downloadSVGLink);
      check();
      const svg = await parse(traced.svg);
      svg.children = svg.children.filter(
        (c) => c.name === "path" && c.attributes?.d?.trim()
      );
      const cropped = cropArtwork(svg, run.longestSide);
      await checkpoint({
        svg: cropped.svg,
        size: cropped.size,
        stage: "analysis",
        progress: "Preparing the layer analysis…",
      });
    }
    if (!run.input) {
      const input = await prepareDesign(
        run.svg,
        run.svg.children.map((c) => c.attributes.fill),
        {},
        {},
        run.size,
        "cotton"
      );
      input.viewBox = run.svg.attributes.viewBox.split(/[ ,]+/).map(Number);
      input.processingMode = run.processingMode || "standard";
      if (run.referenceImage) input.referenceImage = run.referenceImage;
      await checkpoint({ input, jobId: run.id });
    }
    if (!run.result) {
      // Same ID and payload: retrying or reopening never submits a second AI job.
      let job = await jobRequest(
        "/jobs",
        { id: run.jobId, input: run.input },
        signal
      );
      while (!["completed", "failed", "cancelled"].includes(job.state)) {
        check();
        update({
          ...run,
          stage: job.stage || "analysis",
          progress: job.progress || "Waiting for the worker…",
          decisions: job.layers,
        });
        await wait(2000, signal);
        job = await jobRequest(`/jobs/${run.jobId}`, null, signal);
      }
      if (job.usage) await checkpoint({ costUsage: job.usage });
      if (job.state !== "completed")
        throw new Error(
          job.error || "This run was cancelled. Start a new run to try again."
        );
      await checkpoint({
        result: run.generationUsage ? {
          ...job.result,
          usage: { calls: [...(run.generationUsage.calls || []), ...(job.result.usage?.calls || [])],
            requestCount: (run.generationUsage.calls?.length || 0) + (job.result.usage?.calls?.length || 0) },
        } : job.result,
        stage: "stitches",
        progress: "Validating satin rails in the editor…",
      });
    }
    if (!run.artifact) {
      const routes = JSON.parse(JSON.stringify(run.result.layers));
      for (const layer of routes) {
        check();
        if (layer.choice === "satin") {
          report(`Checking rails for layer ${layer.index + 1}…`);
          await wait(32, signal);
          layer.result = await materializeWithRepair(
            layer.result,
            signal,
            report
          );
        }
      }
      const kept = {
        ...run.svg,
        children: run.svg.children.filter(
          (_, index) =>
            routes.find((r) => r.index === index)?.choice !== "background"
        ),
      };
      const cropped = cropArtwork(kept, run.longestSide);
      await checkpoint({
        size: cropped.size,
        artifact: assembleDesign(
          { ...run.svg, attributes: cropped.svg.attributes },
          routes,
          cropped.size
        ),
        stage: run.generateDst === false ? "save" : "export",
        progress:
          run.generateDst === false
            ? "Saving the editable project to your account…"
            : "Generating the DST stitch file…",
      });
    }
    if (run.generateDst !== false && run.artifact.complete !== false && !run.dst) {
      const response = await fetch(`${API}/convert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal,
        body: JSON.stringify({
          svgContent: stringify(run.artifact.exportSvg),
          format: "dst",
          options: {
            collapse_len: 3,
            min_stitch_len: 0.1,
            disable_ties: false,
            skip_simplify: true,
            no_trims: false,
            hoop_width_mm: 100,
            hoop_height_mm: 100,
          },
        }),
      });
      const dst = await response.json();
      if (!response.ok || !dst.success || !dst.data)
        throw new Error(
          dst.error ||
            "DST conversion failed. The prepared geometry is saved for retry."
        );
      const parsed = parseEmbroideryFile(dst.data, "dst");
      if (!parsed?.stitches?.length)
        throw new Error("The converter returned an empty stitch file.");
      if (
        (parsed.maxX - parsed.minX) / 10 > 100 ||
        (parsed.maxY - parsed.minY) / 10 > 100
      )
        throw new Error(
          "The generated stitches exceed the selected 100 mm hoop."
        );
      await checkpoint({
        dst,
        stage: "save",
        progress: "Saving the editable project to your account…",
      });
    }
    if (!run.saved) {
      // Deterministic destinations make account saves safe to retry after a disconnect.
      check();
      const filePath = `${run.uid}/${SVG_FOLDER}/${run.id}.svg`;
      const artworkPath = `${run.uid}/${REFERENCE_ARTWORK_FOLDER}/${run.id}.png`;
      await uploadString(
        ref(storage, filePath),
        stringify(run.artifact.svg),
        "raw",
        { contentType: "image/svg+xml" }
      );
      check();
      await uploadString(ref(storage, artworkPath), run.image, "data_url", {
        contentType: "image/png",
      });
      check();
      let lastGeneratedFile = null;
      if (run.dst) {
        lastGeneratedFile = await saveStitchFileToStorage(
          run.uid,
          run.id,
          "dst",
          run.dst.data,
          {
            metadata: {
              recipe: "trace-auto-digitize-v1",
              colors: dstPreview(run).colors,
            },
          }
        );
        check();
      }
      const stitchState = {
        layerStitchSettings: run.artifact.settings,
        layerRails: run.artifact.rails,
        hoopSettings: {
          selectedHoop: 0,
          targetWidth: run.size.width,
          targetHeight: run.size.height,
        },
        referenceArtwork: {
          path: artworkPath,
          mimeType: "image/png",
          name: run.name,
        },
      };
      if (lastGeneratedFile) stitchState.lastGeneratedFile = lastGeneratedFile;
      await saveStitchStateToFirestore(run.uid, run.id, stitchState);
      check();
      const batch = writeBatch(db);
      const common = {
        isPublic: false,
        type: "uploaded",
        workflow: "stitch",
        seoTitle: run.name,
        seoDescription: "Created with Trace auto-digitizing",
        prompt: "",
        playgroundRecipe: "trace-auto-digitize-v1",
        layerDecisions: run.artifact.decisions,
      };
      batch.set(
        doc(db, METADATA_COLLECTION, run.id),
        {
          ...common,
          filePath,
          ownerUid: run.uid,
          createdAt: new Date(run.startedAt),
          shareLink: "",
          downloadCount: 0,
        },
        { merge: true }
      );
      batch.set(
        doc(db, "accountData", run.uid, FILES_SUBCOLLECTION, run.id),
        {
          ...common,
          id: run.id,
          svgMetadataId: run.id,
          filepath: filePath,
          fileType: "uploaded",
          timeCreated: new Date(run.startedAt),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await batch.commit();
      await checkpoint({
        saved: true,
        filePath,
        artworkPath,
        lastGeneratedFile,
        stage: "done",
        progress:
          run.artifact.complete === false
            ? "Partial design saved. Open the editor to finish the remaining areas."
            : run.generateDst === false
            ? "Saved to your account. Opening the editor…"
            : "Saved to your account. Your DST is ready.",
      });
    }
    return run;
  } catch (error) {
    if (error.name !== "AbortError") {
      run = {
        ...run,
        error:
          error.message ||
          "Auto-digitizing could not finish. Your progress is saved.",
      };
      await playgroundRecord(run.uid, run);
      update(run);
    }
    return run;
  }
}
